import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, Eye, EyeOff } from "lucide-react";
import GlobalToaster from "@/components/GlobalToaster";

/**
 * 重置密码页面 - 设置新密码
 *
 * 通过邮件中的令牌链接访问
 * URL: /app/reset-password?reset_password_token=xxx
 */
export default function ResetPassword() {
  const { t } = useTranslation("login");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get("reset_password_token");

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 如果没有令牌，提示错误
  if (!resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[url('/images/bg-white.png')] bg-repeat bg-fixed dark:bg-[url('/images/bg-dark.png')] p-4">
        <GlobalToaster />
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-red-600 dark:text-red-400">
              {t("resetPassword.invalidLink")}
            </CardTitle>
            <CardDescription>
              {t("resetPassword.invalidLinkDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/login")}>
              {t("resetPassword.backToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证密码
    if (!password || password.length < 6) {
      toast.error(t("resetPassword.passwordTooShort"));
      return;
    }

    if (password !== passwordConfirmation) {
      toast.error(t("resetPassword.passwordMismatch"));
      return;
    }

    setIsLoading(true);

    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";

      const response = await fetch("/users/password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          user: {
            reset_password_token: resetToken,
            password: password,
            password_confirmation: passwordConfirmation,
          },
        }),
      });

      if (response.ok) {
        toast.success(t("resetPassword.success"));
        // 延迟跳转到登录页
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        const data = await response.json();
        toast.error(data.error || t("resetPassword.failed"));
      }
    } catch {
      toast.error(t("common.networkError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t("resetPassword.title")}</CardTitle>
          <CardDescription className="text-base">
            {t("resetPassword.subtitle")}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 新密码输入 */}
            <div className="space-y-2">
              <Label htmlFor="password">{t("resetPassword.newPassword")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("resetPassword.newPasswordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  disabled={isLoading}
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("resetPassword.newPasswordHint")}
              </p>
            </div>

            {/* 确认密码输入 */}
            <div className="space-y-2">
              <Label htmlFor="password_confirmation">
                {t("resetPassword.confirmPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="password_confirmation"
                  type={showPasswordConfirmation ? "text" : "password"}
                  placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowPasswordConfirmation(!showPasswordConfirmation)
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPasswordConfirmation ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* 提交按钮 */}
            <Button type="submit" className="w-full h-11" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {t("resetPassword.submitting")}
                </>
              ) : (
                t("resetPassword.submit")
              )}
            </Button>

            {/* 安全提示 */}
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>{t("resetPassword.securityTip")}</strong>{" "}
                {t("resetPassword.securityTipContent")}
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
